import { provideHttpClient } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler } from '@angular/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getMessaging, provideMessaging } from '@angular/fire/messaging';
import { getRemoteConfig, provideRemoteConfig } from '@angular/fire/remote-config';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, TitleStrategy, withInMemoryScrolling, withPreloading } from '@angular/router';
import { provideAppActions } from '@app/core/app.actions';
import { CustomTitleStrategy } from '@app/core/navigation/app-title.provider';
import { provideInitializeRemoteConfig } from '@app/core/providers/remote-config.provider';
import { provideInitialPopup } from '@app/core/providers/required-popup.provider';
import { provideTranslations } from '@app/core/providers/translation.provider';
import { provideInitializeWebsocket } from '@app/core/providers/websocket.provider';
import { TranslocoHttpLoader } from '@app/core/transloco/transloco-http.loader';
import { TranslocoMissingHandlerLoader, TranslocoTranspilerLoader } from '@app/core/transloco/transloco-module.loader';
import { provideTransloco, provideTranslocoMissingHandler, provideTranslocoTranspiler } from '@ngneat/transloco';
import { provideSeal } from '@shipsfocus/seal';
import { appRoutes } from 'app/app.routes';
import { CustomLuxonDateAdapter } from 'app/core/adapter/date.adapter';
import { provideAuth } from 'app/core/auth/auth.provider';
import { GlobalErrorInterceptor } from 'app/core/guard/global-error.interceptor';
import { provideIcons } from 'app/core/icons/icons.provider';
import { mockApiServices } from 'app/mock-api';
import { environment } from 'environments/environment';
import { provideNgxMask } from 'ngx-mask';
import { quicklinkProviders, QuicklinkStrategy } from 'ngx-quicklink';
import { provideToastr } from 'ngx-toastr';
import { ServiceEnum } from './core/constants';

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideToastr(),
    provideHttpClient(),
    {
      provide: ErrorHandler,
      useClass: GlobalErrorInterceptor
    },
    provideRouter(appRoutes, withPreloading(QuicklinkStrategy), withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })),
    quicklinkProviders,
    {
      provide: DateAdapter,
      useClass: CustomLuxonDateAdapter
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: 'dd LLL yyyy'
        },
        display: {
          dateInput: 'dd LLL yyyy',
          monthYearLabel: 'LLL yyyy',
          dateA11yLabel: 'dd',
          monthYearA11yLabel: 'LLLL yyyy'
        }
      }
    },
    provideTransloco({
      config: {
        availableLangs:
          environment.SERVICE_ID !== ServiceEnum.TABUCHI
            ? [
                {
                  id: 'en',
                  label: 'English'
                },
                {
                  id: 'kr',
                  label: 'Korean'
                },
                {
                  id: 'jp',
                  label: 'Japanese'
                },
                {
                  id: 'zh',
                  label: 'Chinese'
                }
              ]
            : [
                {
                  id: 'en',
                  label: 'English'
                },
                {
                  id: 'jp',
                  label: 'Japanese'
                }
              ],
        defaultLang: 'en',
        fallbackLang: 'en',
        reRenderOnLangChange: true,
        prodMode: true
      },
      loader: TranslocoHttpLoader
    }),
    provideTranslations(),
    provideTranslocoMissingHandler(TranslocoMissingHandlerLoader),
    provideTranslocoTranspiler(TranslocoTranspilerLoader),
    provideAuth(),
    provideInitializeWebsocket(),
    provideIcons(),
    provideNgxMask(),
    provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
    provideMessaging(() => getMessaging()),
    provideRemoteConfig(() => getRemoteConfig()),
    provideInitializeRemoteConfig(),
    provideSeal({
      mockApi: {
        delay: 0,
        services: mockApiServices
      },
      seal: {
        layout: 'modern',
        scheme: 'light',
        screens: {
          sm: '600px',
          md: '960px',
          lg: '1280px',
          xl: '1440px'
        },
        theme: 'theme-default',
        themes: [
          {
            id: 'theme-default',
            name: 'setting.theme.default-color'
          },
          {
            id: 'theme-brand',
            name: 'setting.theme.brand'
          },
          {
            id: 'theme-teal',
            name: 'setting.theme.teal'
          },
          {
            id: 'theme-rose',
            name: 'setting.theme.rose'
          },
          {
            id: 'theme-purple',
            name: 'setting.theme.purple'
          },
          {
            id: 'theme-amber',
            name: 'setting.theme.amber'
          }
        ]
      }
    }),
    provideInitialPopup(),
    provideAppActions(),
    {
      provide: TitleStrategy,
      useClass: CustomTitleStrategy
    }
  ]
};
